import axios from "axios";
import { useContext, useEffect, useRef } from "react";
import Swal from "sweetalert2";
import { AuthContext } from "../contexts/AuthProvider";

const FailedPaymentMonitor = () => {
  const { user, userInfo } = useContext(AuthContext);
  const modalShownRef = useRef(false);

  useEffect(() => {
    // Check for failed payments on app load
    const checkFailedPayments = () => {
      if (modalShownRef.current) {
        console.log("Modal already shown, skipping...");
        return;
      }
      try {
        const failedPayment = localStorage.getItem("failedPayment");
        const failedBundlePayment = localStorage.getItem("failedBundlePayment");

        console.log("FailedPaymentMonitor: Checking for failed payments...");
        console.log("Failed payment:", failedPayment ? "Found" : "None");
        console.log(
          "Failed bundle payment:",
          failedBundlePayment ? "Found" : "None"
        );

        if (failedPayment || failedBundlePayment) {
          const paymentData = failedPayment
            ? JSON.parse(failedPayment)
            : JSON.parse(failedBundlePayment);

          console.log("Payment data:", paymentData);

          // Mark that we're showing the modal
          modalShownRef.current = true;

          // Show modal with stronger persistence
          const showPaymentModal = () => {
            console.log("Attempting to show payment modal...");

            Swal.fire({
              title: "Your Payment is In Progress",
              html: `
                <p>Your payment for ₹${paymentData.paidAmount} (Payment ID: ${paymentData.paymentId}) is in progress. Basis the payment status, your course(s) access should be refreshed within the next few hours</p>
                <p>Thanks for your patience.</p>
              `,
              icon: "warning",
              showCancelButton: true,
              confirmButtonText: "Okay",
              allowOutsideClick: false,
              allowEscapeKey: false,
              showCloseButton: true,
              focusConfirm: false,
              heightAuto: false,
              backdrop: true,
              customClass: {
                container: "payment-modal-container",
                popup: "payment-modal-popup",
              },
              didOpen: () => {
                // Prevent any other scripts from closing this modal
                console.log("Payment modal opened successfully");
              },
              didClose: () => {
                console.log("Payment modal was closed");
              },
            })
              .then((result) => {
                console.log("Modal result:", result);

                if (result.isConfirmed || result.dismiss === Swal.DismissReason.close) {
                  // Handle both OK button and close button (X) - call API
                  handleRetryPayment(
                    paymentData,
                    failedBundlePayment ? "bundle" : "course"
                  );
                } else if (result.dismiss === Swal.DismissReason.cancel) {
                  // Show support contact info
                  Swal.fire({
                    title: "Contact Support",
                    html: `
                    <p>Please contact support with this information:</p>
                    <p><strong>Payment ID:</strong> ${paymentData.paymentId}</p>
                    <p><strong>Order ID:</strong> ${paymentData.orderId}</p>
                    <p><strong>Email:</strong> ${paymentData.email}</p>
                  `,
                    icon: "info",
                    allowOutsideClick: true,
                    showCloseButton: true,
                  });
                }
              })
              .catch((error) => {
                console.error("Modal error:", error);
                // If modal fails, try again after a short delay
                setTimeout(showPaymentModal, 2000);
              });
          };

          // Try to show modal, with fallback if it gets closed unexpectedly
          showPaymentModal();
        }
      } catch (error) {
        console.error("Error in FailedPaymentMonitor:", error);
      }
    };

    // Try multiple times to ensure the modal shows when the app is ready
    const attemptTimes = [3000, 5000, 7000, 9000]; // Try at 3s, 5s, 7s, 9s

    attemptTimes.forEach((delay) => {
      setTimeout(() => {
        checkFailedPayments();
      }, delay);
    });
  }, []);

  const handleRetryPayment = (paymentData, type) => {
    // Show loading message
    Swal.fire({
      title: "Retrying Payment Verification...",
      text: "Please wait while we verify your payment.",
      icon: "info",
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    // Call the actual retry function
    retryPaymentVerification(paymentData, type);
  };

  const retryPaymentVerification = async (paymentData, type) => {
    try {
      const endpoint =
        type === "bundle"
          ? "/api/v1/users/unpaidUsers/verifyBundlePayment"
          : "/api/v1/users/unpaidUsers/verifyPayment";

      let requestData;

      if (type === "bundle") {
        // For bundle payments - match what BundlePayment.js sends
        requestData = {
          razorpay_payment_id: paymentData.paymentId,
          razorpay_order_id: paymentData.orderId,
          razorpay_signature: paymentData.razorpay_signature, // This might be missing from localStorage
          razorpay_key_secret: paymentData.razorpay_key_secret, // This might be missing from localStorage
          courses: paymentData.courses,
          bundleId: paymentData.bundleId,
          email: paymentData.email,
          userId: paymentData.userId,
          paidAmount: paymentData.paidAmount,
          originalPrice: paymentData.originalPrice,
          discountAmount: paymentData.discountAmount,
          coupon: paymentData.coupon,
          couponId: paymentData.couponId,
          organizationId: paymentData.organizationId,
          organizationName: paymentData.organizationName,
        };
      } else {
        // For single course payments - match what Payment.js sends
        requestData = {
          razorpay_payment_id: paymentData.paymentId,
          razorpay_order_id: paymentData.orderId,
          razorpay_signature: paymentData.razorpay_signature, // This might be missing from localStorage
          razorpay_key_secret: paymentData.razorpay_key_secret, // This might be missing from localStorage
          courseId: paymentData.courseId,
          batchId: paymentData.batchId,
          email: paymentData.email,
          userId: paymentData.userId,
          paidAmount: paymentData.paidAmount,
          originalPrice: paymentData.originalPrice,
          discountAmount: paymentData.discountAmount,
          coupon: paymentData.coupon,
          couponId: paymentData.couponId,
          organizationId: paymentData.organizationId,
          organizationName: paymentData.organizationName,
        };
      }

      // Try to get email from current user if missing from payment data
      if (!requestData.email) {
        requestData.email = userInfo?.email || user?.email;
      }

      // Check critical fields only
      const missingFields = [];
      if (!requestData.razorpay_payment_id) missingFields.push("payment_id");
      if (!requestData.razorpay_order_id) missingFields.push("order_id");
      if (!requestData.email) missingFields.push("email");

      console.log("Request data for retry:", requestData);

      if (missingFields.length > 0) {
        console.warn(`Missing critical fields: ${missingFields.join(", ")}`);
        throw new Error(
          `Missing critical fields: ${missingFields.join(
            ", "
          )}. Please contact support.`
        );
      }

      // Warn about optional missing fields but don't fail
      const optionalMissingFields = [];
      if (!requestData.razorpay_signature)
        optionalMissingFields.push("signature");
      if (!requestData.razorpay_key_secret)
        optionalMissingFields.push("key_secret");
      if (!requestData.userId) optionalMissingFields.push("userId");
      if (!requestData.organizationId)
        optionalMissingFields.push("organizationId");

      if (optionalMissingFields.length > 0) {
        console.warn(
          `Optional fields missing: ${optionalMissingFields.join(", ")}`
        );
      }

      const response = await axios.post(
        `${process.env.REACT_APP_SERVERLESS_API}${endpoint}`,
        requestData
      );

      if (response.data.success) {
        // Clear failed payment and show success
        localStorage.removeItem(
          type === "bundle" ? "failedBundlePayment" : "failedPayment"
        );

        Swal.fire({
          title: "Payment Verified!",
          text: `Your ${
            type === "bundle" ? "courses have" : "course has"
          } been successfully enrolled.`,
          icon: "success",
          allowOutsideClick: true,
          showCloseButton: true,
        }).then(() => {
          window.location.href = "/courseAccess";
        });
      } else {
        throw new Error("Payment verification still failed");
      }
    } catch (error) {
      console.error("Retry payment verification error:", error);

      // Show user-friendly error message
      Swal.fire({
        title: "Retry Failed",
        html: `
          <p>Unable to retry verification: ${
            error.message || "Unknown error"
          }</p>
          <p>Please contact support with this payment information:</p>
          <p><strong>Payment ID:</strong> ${paymentData.paymentId || "N/A"}</p>
          <p><strong>Email:</strong> ${paymentData.email || "N/A"}</p>
        `,
        icon: "error",
        confirmButtonText: "OK",
        allowOutsideClick: true,
        showCloseButton: true,
      });
    }
  };

  return null; // This component doesn't render anything
};

export default FailedPaymentMonitor;
